/**
 * The support gate. Ships now, not later.
 *
 * The reason this file exists in M1 rather than M3 is that the first demo
 * happens on somebody's phone. iOS Safari suspends WebRTC *and* Web Audio when
 * the screen locks or the tab backgrounds, and the microphone track goes `muted`
 * with no JS workaround — no wake lock, no silent-audio trick, nothing. A call
 * that dies thirty seconds into the demo because someone glanced at a
 * notification is indistinguishable, to the room, from our architecture not
 * working. So iOS shipped as a HARD BLOCK with an explicit message.
 *
 * IT IS A WARNING NOW, NOT A BLOCK (2026-09-04). Two things changed. The first
 * public page (gene.4js.ai) put the widget in front of people holding phones, and
 * a phone held to the face with the screen on is exactly the case the block never
 * needed to refuse — the suspension bites when the screen locks or the visitor
 * switches apps, not while they are talking. And docs/research.md (U9) rates the
 * suspension claim itself as UNVERIFIED against Apple's own documentation, owed a
 * device test rather than a citation chain; a refusal cannot be tested and a
 * warning can. So iOS starts the call and carries the caveat as the first warning,
 * in the same list the recorder degradations use. If the device test shows calls
 * dying with the screen on, `isIosPlatform()` below is the one line to turn back.
 *
 * EVERYTHING ELSE IS FEATURE-DETECTED. There is exactly one platform check in
 * this file and it is the iOS warning, because "suspends media on screen lock" has
 * no capability to test for — it is a behaviour, not an API. Every other
 * decision here asks the browser what it can do. Sniffing Chrome to enable
 * Encoded Transform would break the day Firefox ships it, which it now has.
 *
 * The gate is deliberately GENEROUS about the recorder and STRICT about the
 * call: a browser with no Encoded Transform, no WebCodecs and no OPFS can still
 * hold a conversation, and that conversation is the product. It just cannot
 * produce evidence, and the UI says so instead of silently recording nothing.
 */

export interface SupportReport {
  /** Can we start a call at all? */
  ok: boolean
  /** Non-null means refuse to start, and show this to the human verbatim. */
  blocked: string | null
  /** Best available capture path. `none` still allows a call. */
  recorderPath: 'encoded-transform' | 'webcodecs-opus' | 'pcm16' | 'none'
  /** Things that work. */
  have: {
    secureContext: boolean
    webrtc: boolean
    getUserMedia: boolean
    encodedTransform: boolean
    webCodecsOpus: boolean
    audioWorklet: boolean
    opfs: boolean
    webLocks: boolean
    workerModules: boolean
  }
  /** Human-readable consequences of what is missing. Rendered as a list. */
  warnings: string[]
}

/**
 * iPhone and iPad, any browser.
 *
 * Every iOS browser is WebKit under the hood, so this is a platform gate and not
 * a browser gate. iPadOS 13+ reports itself as `MacIntel` with a touch screen,
 * which is the only reliable way to separate an iPad from a Mac — a real Mac
 * reports `maxTouchPoints === 0` even with a trackpad.
 */
function isIosPlatform(): boolean {
  const nav = navigator
  const platform = nav.platform ?? ''
  if (/^(iPhone|iPad|iPod)/i.test(platform)) return true
  if (platform === 'MacIntel' && nav.maxTouchPoints > 1) return true
  // Last resort, and only for the platform question: iOS UA strings all carry
  // one of these tokens regardless of which browser wrapper is in front.
  return /iPhone|iPad|iPod/i.test(nav.userAgent) && !/Macintosh/i.test(nav.userAgent)
}

/** Shown on iPhone and iPad as the first warning. It names the one thing the
 *  visitor can do about it, and says nothing about a microphone — the old refusal
 *  did, and the widget's error mapper read that word as a hardware fault. */
const IOS_WARNING =
  'On iPhone and iPad the call runs while the screen is on and this tab is in ' +
  'front. iOS suspends WebRTC and Web Audio when the screen locks or you switch ' +
  'apps, and a page cannot prevent it — lock the screen mid-sentence and the call ' +
  'ends. Keep the screen on for the whole conversation.'

/** Does this browser have a WebCodecs Opus encoder that will actually configure? */
async function probeWebCodecsOpus(): Promise<boolean> {
  const ctor = (globalThis as { AudioEncoder?: { isConfigSupported?: (c: unknown) => Promise<{ supported?: boolean }> } })
    .AudioEncoder
  if (!ctor || typeof ctor.isConfigSupported !== 'function') return false
  try {
    const res = await ctor.isConfigSupported({
      codec: 'opus',
      sampleRate: 48000,
      numberOfChannels: 1,
      bitrate: 24000,
    })
    return res.supported === true
  } catch {
    // isConfigSupported rejects rather than resolving false on some builds.
    return false
  }
}

export async function probe(): Promise<SupportReport> {
  const warnings: string[] = []

  const secureContext = globalThis.isSecureContext === true
  const webrtc = typeof RTCPeerConnection === 'function'
  const getUserMedia = typeof navigator.mediaDevices?.getUserMedia === 'function'
  const encodedTransform = typeof (globalThis as { RTCRtpScriptTransform?: unknown }).RTCRtpScriptTransform === 'function'
  const audioWorklet = typeof AudioContext === 'function' && 'audioWorklet' in AudioContext.prototype
  const opfs = typeof navigator.storage?.getDirectory === 'function'
  // NOT DETECTABLE FROM HERE, AND THIS USED TO CLAIM OTHERWISE.
  //
  // `createSyncAccessHandle` is [Exposed=DedicatedWorker]. It is absent from
  // `FileSystemFileHandle.prototype` on the window in EVERY browser, including
  // ones where OPFS staging works perfectly — so probing for it on the main
  // thread returned false always, and the "no OPFS synchronous access handle"
  // warning below fired on every call on every browser. It was pure noise sitting
  // on the call screen, and worse: it named a real failure mode, so it trained
  // whoever read it to ignore the one message that would have meant something.
  //
  // The honest answer comes from the place that actually opens the handle:
  // `Uploader.openStaging()` runs in the sink worker and reports
  // "OPFS is unavailable; chunks will be staged in memory only" or
  // "OPFS staging could not be opened (…); using memory". That is a measurement
  // rather than a guess, so this probe reports only what the main thread can
  // genuinely see — whether OPFS exists at all — and leaves the rest to runtime.
  // (No `syncAccessHandle` capability is reported: see above — it cannot be
  //  measured here, and a field that silently restated `opfs` under a name
  //  promising more would be the same lie in miniature.)
  const webLocks = typeof navigator.locks?.request === 'function'
  // A module worker is not optional: both of our workers use `import`. Older
  // Safari silently ignored `{ type: 'module' }` and then failed on the first
  // import, which reads as "the recorder does nothing" rather than as an error.
  const workerModules = typeof Worker === 'function'
  const webCodecsOpus = await probeWebCodecsOpus()

  let blocked: string | null = null
  if (!secureContext)
    blocked =
      'This page must be served over HTTPS (or from localhost). Microphone ' +
      'access and WebRTC are unavailable in an insecure context.'
  else if (!webrtc || !getUserMedia)
    // The commonest way to land here on a phone is an in-app browser (a link
    // opened inside a chat or social app), which has no getUserMedia at all.
    blocked =
      'This browser has no WebRTC or no microphone API, so it cannot hold a ' +
      'call. Open the page in Chrome, Edge or Safari.'

  // First in the list on purpose: it is the one warning about the CALL rather
  // than about the recording, and the one the visitor can act on.
  if (isIosPlatform()) warnings.push(IOS_WARNING)

  let recorderPath: SupportReport['recorderPath'] = 'none'
  if (encodedTransform) recorderPath = 'encoded-transform'
  else if (audioWorklet && webCodecsOpus) recorderPath = 'webcodecs-opus'
  else if (audioWorklet) recorderPath = 'pcm16'

  // encoded-transform is the BEST path and deliberately pushes no warning: the
  // rendition-vs-wire-bytes nuance it used to explain belongs in docs, not on a
  // screen whose reader just wants to talk. The two paths below are genuine
  // degradations, and those stay worth a sentence.
  if (recorderPath === 'webcodecs-opus') {
    warnings.push(
      'No WebRTC Encoded Transform in this browser, so audio is captured with a ' +
        'Web Audio tap and re-encoded. The recording is a faithful rendition, ' +
        'but it is not the bytes that crossed the network — for the assistant ' +
        'track it is this connection’s decoded playout, jitter buffer and ' +
        'packet-loss concealment included.',
    )
  }
  if (recorderPath === 'pcm16') {
    warnings.push(
      'Neither Encoded Transform nor a WebCodecs Opus encoder is available, so ' +
        'capture falls back to 16 kHz PCM. That is roughly thirteen times the ' +
        'bytes for a narrower band, and the same re-encode caveat applies.',
    )
  }
  if (recorderPath === 'none') {
    warnings.push(
      'This browser cannot capture audio at all from a page. The conversation ' +
        'will work — media goes straight to the provider — but there will be no ' +
        'recording and the session will finalise with no evidence.',
    )
  }
  if (!opfs) {
    warnings.push(
      'No OPFS in this browser, so recorded chunks cannot be staged to disk. A ' +
        'dropped media uplink becomes real loss instead of latency.',
    )
  }
  if (!webLocks) {
    warnings.push(
      'No Web Locks API. If this session were somehow opened twice, both tabs ' +
        'would upload the same recording.',
    )
  }

  return {
    ok: blocked === null,
    blocked,
    recorderPath,
    have: {
      secureContext,
      webrtc,
      getUserMedia,
      encodedTransform,
      webCodecsOpus,
      audioWorklet,
      opfs,
      webLocks,
      workerModules,
    },
    warnings,
  }
}
